import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Design tokens — readability-first, dark premium
const C = {
  bg: '#080C14',
  text: '#E8ECEF',       // primary text
  muted: '#A8B0B8',      // secondary text
  faint: '#6B737D',      // tertiary (labels)
  accent: '#C89B7B',     // copper gold
  accentDark: '#B88A6A',
  border: 'rgba(255,255,255,0.08)',
  cardBg: 'rgba(255,255,255,0.02)',
};

/* ─── HEADER ─── */
function Header() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-10 h-[68px] flex items-center justify-between"
      style={{
        background: 'rgba(8, 12, 20, 0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${C.border}`,
      }}
      data-testid="landing-header"
    >
      <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
        <img
          src="/brand/regenflo-wordmark.png"
          alt="ReGenflō"
          className="h-7 sm:h-8 w-auto object-contain select-none"
          style={{ filter: 'drop-shadow(0 0 14px rgba(200,155,123,0.08))' }}
          draggable={false}
        />
        <span
          className="hidden sm:inline text-[11px] uppercase font-['Outfit']"
          style={{ color: C.faint, letterSpacing: '4px' }}
        >
          Care OS
        </span>
      </Link>

      <Link to="/login" data-testid="client-login-link" className="group flex items-center gap-2">
        <span
          className="font-['Outfit'] font-light text-sm tracking-wider transition-colors"
          style={{ color: C.muted }}
        >
          Existing client?
        </span>
        <span
          className="font-['Outfit'] font-medium text-sm tracking-wider transition-colors group-hover:text-[#C89B7B]"
          style={{ color: C.text }}
        >
          Sign in
        </span>
      </Link>
    </header>
  );
}

/* ─── INLINE LOGIN (primary action for existing clients) ─── */
function InlineLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error || 'Login failed.');
      setLoading(false);
      return;
    }
    if (result.requires_password_change) {
      navigate('/change-password-required');
      return;
    }
    const role = result.user?.role;
    if (role === 'admin' || ['counselor', 'health_coach', 'doctor'].includes(role)) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.2 }}
      className="w-full max-w-md mx-auto mt-10 p-7 rounded-sm"
      style={{
        background: 'rgba(255,255,255,0.025)',
        border: `1px solid ${C.border}`,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: '0 20px 60px -20px rgba(0,0,0,0.5), 0 0 30px -10px rgba(200,155,123,0.08)',
      }}
      data-testid="inline-login-card"
    >
      <div className="text-center mb-5">
        <p
          className="font-['Outfit'] font-medium"
          style={{ color: C.text, fontSize: '18px', letterSpacing: '0.01em' }}
        >
          Client Access
        </p>
        <p
          className="font-['Outfit'] font-light mt-1"
          style={{ color: C.muted, fontSize: '13px', letterSpacing: '0.02em' }}
        >
          Sign in to continue your journey
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="inline-login-form">
        <div>
          <label
            className="font-['Outfit'] block mb-1.5"
            style={{ color: C.muted, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase' }}
          >
            Email
          </label>
          <Input
            data-testid="inline-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="your@email.com"
            className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D] rounded-none"
            style={{
              background: 'rgba(255,255,255,0.02)',
              borderColor: C.border,
              color: C.text,
              fontSize: '15px',
              height: '44px',
            }}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label
              className="font-['Outfit']"
              style={{ color: C.muted, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase' }}
            >
              Password
            </label>
            <Link
              to="/forgot-password"
              className="font-['Outfit'] transition-colors hover:text-[#C89B7B]"
              style={{ color: C.muted, fontSize: '11px', letterSpacing: '0.03em' }}
              data-testid="inline-login-forgot"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            data-testid="inline-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
            className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D] rounded-none"
            style={{
              background: 'rgba(255,255,255,0.02)',
              borderColor: C.border,
              color: C.text,
              fontSize: '15px',
              height: '44px',
            }}
          />
        </div>

        {error && (
          <p
            className="font-['Outfit']"
            style={{ color: '#E88B7B', fontSize: '13px', lineHeight: 1.5 }}
            data-testid="inline-login-error"
          >
            {error}
          </p>
        )}

        <Button
          data-testid="inline-login-submit"
          type="submit"
          disabled={loading}
          className="w-full font-['Outfit'] font-medium rounded-none"
          style={{
            backgroundColor: C.accent,
            color: '#080C14',
            fontSize: '15px',
            height: '48px',
            letterSpacing: '2px',
          }}
        >
          {loading ? 'Signing in…' : 'Login'}
        </Button>
      </form>
    </motion.div>
  );
}

/* ─── HERO ─── */
function Hero({ onEnter, onUnderstand }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 600);
    const t2 = setTimeout(() => setStep(2), 2200);
    const t3 = setTimeout(() => setStep(3), 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <section
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-6 pt-[68px]"
      data-testid="hero-section"
    >
      {/* Biological glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #C89B7B 0%, transparent 70%)', animation: 'pulse 9s ease-in-out infinite' }}
        />
        <div
          className="absolute top-1/3 left-1/3 w-[360px] h-[360px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #4E7A6B 0%, transparent 70%)', animation: 'pulse 13s ease-in-out infinite 2s' }}
        />
      </div>

      {/* Subtle Lifely symbol — identity anchor, not decoration */}
      <motion.img
        src="/brand/lifely-symbol.png"
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 0.08, scale: 1 }}
        transition={{ duration: 2.2, ease: 'easeOut' }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] sm:w-[420px] pointer-events-none select-none"
        style={{ filter: 'drop-shadow(0 0 30px rgba(78,122,107,0.25))' }}
        draggable={false}
      />

      <div className="relative z-10 max-w-3xl text-center">
        <AnimatePresence>
          {step >= 1 && (
            <motion.p
              key="s1"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="font-['Outfit'] font-light"
              style={{ color: C.muted, fontSize: '18px', lineHeight: 1.6, letterSpacing: '0.01em' }}
            >
              Most people are trying to fix symptoms.
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step >= 2 && (
            <motion.p
              key="s2"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="font-['Outfit'] font-light mt-6"
              style={{ color: C.text, fontSize: '22px', lineHeight: 1.55, letterSpacing: '0.005em' }}
            >
              The problem is not the symptom.
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step >= 3 && (
            <motion.div
              key="s3"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1 }}
              className="mt-8"
            >
              <h1
                className="font-['Outfit'] font-medium"
                style={{
                  color: C.accent,
                  fontSize: 'clamp(32px, 5vw, 44px)',
                  lineHeight: 1.25,
                  letterSpacing: '0.005em',
                }}
                data-testid="hero-headline"
              >
                The system is misaligned.
              </h1>

              {/* PRIMARY ACTION — existing clients land here and login in 3 seconds */}
              <InlineLogin />

              {/* Secondary path — new users requesting access */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-10">
                <span
                  className="font-['Outfit'] font-light"
                  style={{ color: C.faint, fontSize: '13px', letterSpacing: '0.03em' }}
                >
                  Not a client yet?
                </span>
                <Button
                  data-testid="enter-system-btn"
                  onClick={onEnter}
                  variant="ghost"
                  className="font-['Outfit'] font-medium rounded-none"
                  style={{
                    color: C.text,
                    fontSize: '14px',
                    height: '40px',
                    padding: '0 18px',
                    letterSpacing: '1.5px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                  }}
                >
                  Request Access
                </Button>
                <button
                  type="button"
                  data-testid="understand-btn"
                  onClick={onUnderstand}
                  className="font-['Outfit'] font-light transition-colors hover:text-[#C89B7B]"
                  style={{
                    color: C.muted,
                    fontSize: '13px',
                    letterSpacing: '0.03em',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    textDecorationColor: 'rgba(168,176,184,0.25)',
                    background: 'transparent',
                  }}
                >
                  Understand first
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.05; } 50% { transform: scale(1.15); opacity: 0.08; } }`}</style>
    </section>
  );
}

/* ─── REALITY BREAK ─── */
function RealityBreak() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const cards = [
    'More treatment \u2260 more stability',
    'Normal reports \u2260 healthy system',
    'Symptoms are late-stage signals',
    'The body is not fragmented',
    'Energy is a systems outcome',
    'Recovery is not linear',
  ];

  return (
    <section
      ref={ref}
      id="reality-break"
      className="px-6 py-16 sm:py-20"
      data-testid="reality-break-section"
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((text, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: i * 0.15 }}
            className="group p-7 rounded-sm transition-all duration-500 cursor-default"
            style={{
              background: C.cardBg,
              border: `1px solid ${C.border}`,
            }}
            whileHover={{
              borderColor: 'rgba(200,155,123,0.35)',
              boxShadow: '0 0 40px -10px rgba(200,155,123,0.18)',
            }}
          >
            <p
              className="font-['Outfit'] font-light"
              style={{
                color: C.text,
                fontSize: '16px',
                lineHeight: 1.6,
                letterSpacing: '0.005em',
              }}
            >
              {text}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─── SYSTEM AXES ─── */
function SystemAxes() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const axes = [
    { code: 'BAS', meaning: 'Your current biological state' },
    { code: 'NAS', meaning: 'Your neurological behaviour pattern' },
    { code: 'TAS', meaning: 'Accumulated terrain & toxic load' },
  ];

  return (
    <section ref={ref} className="px-6 py-16 sm:py-20" data-testid="system-axes-section">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-20">
        {axes.map((a, i) => (
          <motion.div
            key={a.code}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: i * 0.25 }}
            className="group text-center cursor-default"
          >
            <p
              className="font-['Outfit'] font-light transition-colors duration-500 group-hover:text-[#C89B7B]"
              style={{
                color: C.text,
                fontSize: 'clamp(32px, 4vw, 40px)',
                letterSpacing: '8px',
                lineHeight: 1.1,
              }}
            >
              {a.code}
            </p>
            <p
              className="font-['Outfit'] mt-3"
              style={{
                color: C.muted,
                fontSize: '14px',
                letterSpacing: '0.02em',
                lineHeight: 1.5,
              }}
            >
              {a.meaning}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─── SYSTEM FLOW ─── */
function SystemFlow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const nodes = ['Decode', 'Align', 'Stabilize', 'Execute'];

  return (
    <section ref={ref} className="px-6 py-16 sm:py-20" data-testid="system-flow-section">
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <div className="flex items-center justify-center gap-3 sm:gap-6 flex-wrap">
          {nodes.map((n, i) => (
            <React.Fragment key={n}>
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.2 }}
                className="font-['Outfit'] font-light"
                style={{
                  color: C.text,
                  fontSize: '16px',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  animation: inView ? `floatNode 6s ease-in-out infinite ${i * 0.8}s` : 'none',
                }}
              >
                {n}
              </motion.div>
              {i < nodes.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={inView ? { opacity: 1, scaleX: 1 } : {}}
                  transition={{ duration: 0.4, delay: i * 0.2 + 0.12 }}
                  className="w-10 sm:w-16 h-px"
                  style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.18), rgba(255,255,255,0.04))' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="font-['Outfit'] mt-8"
          style={{ color: C.muted, fontSize: '14px', letterSpacing: '3px' }}
        >
          No two journeys are identical.
        </motion.p>
      </div>
      <style>{`@keyframes floatNode { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }`}</style>
    </section>
  );
}

/* ─── SELF RECOGNITION ─── */
function SelfRecognition() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const lines = [
    "You've tried multiple solutions, but nothing holds",
    "Your reports look normal, but you don't feel normal",
    'Your symptoms shift, not resolve',
    'Your energy is inconsistent without reason',
  ];

  return (
    <section ref={ref} className="px-6 py-16 sm:py-20" data-testid="self-recognition-section">
      <div className="max-w-xl mx-auto text-center space-y-7">
        {lines.map((line, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: i * 0.4 }}
            className="font-['Outfit'] font-light"
            style={{
              color: C.text,
              fontSize: '18px',
              lineHeight: 1.65,
              letterSpacing: '0.005em',
            }}
          >
            {line}
          </motion.p>
        ))}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1.9 }}
          className="font-['Outfit'] font-medium pt-4"
          style={{
            color: C.accent,
            fontSize: '22px',
            lineHeight: 1.5,
            letterSpacing: '0.005em',
          }}
        >
          Your system is not being understood.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── PHILOSOPHY ─── */
function Philosophy() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const lines = [
    { text: 'CARE OS does not treat conditions.', color: C.text },
    { text: 'It maps and interacts with biological systems.', color: C.text },
    { text: 'It does not override your body.', color: C.text },
    { text: 'It restores its intelligence.', color: C.accent, weight: 500 },
  ];

  return (
    <section ref={ref} className="px-6 py-16 sm:py-20" data-testid="philosophy-section">
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1.2 }}
        className="max-w-xl mx-auto text-center space-y-5"
      >
        {lines.map((l, i) => (
          <p
            key={i}
            className="font-['Outfit']"
            style={{
              color: l.color,
              fontSize: '17px',
              lineHeight: 1.75,
              letterSpacing: '0.005em',
              fontWeight: l.weight || 300,
            }}
          >
            {l.text}
          </p>
        ))}
      </motion.div>
    </section>
  );
}

/* ─── ACCESS GATE ─── */
function AccessGate() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [form, setForm] = useState({ name: '', email: '', age: '', city: '', condition: '', what_tried: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.what_tried) {
      toast.error('Please complete all required fields'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/inquiries`, {
        name: form.name,
        email: form.email,
        city: form.city || undefined,
        age: form.age ? parseInt(form.age) : undefined,
        condition: form.condition || undefined,
        what_tried: form.what_tried,
        source: 'access_gate',
        lead_source: 'website',
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('already exists')) { toast.error('A request with this email already exists.'); }
      else { toast.error(detail || 'Submission failed. Please try again.'); }
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <section className="px-6 py-20 sm:py-24 flex items-center justify-center" data-testid="access-gate-section">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-md text-center space-y-5"
          data-testid="access-gate-confirmed"
        >
          <div className="w-12 h-px mx-auto" style={{ background: C.accent, opacity: 0.4 }} />
          <p className="font-['Outfit'] font-light" style={{ color: C.text, fontSize: '18px', lineHeight: 1.6 }}>
            Your case will be reviewed.
          </p>
          <p className="font-['Outfit'] font-light" style={{ color: C.muted, fontSize: '15px', lineHeight: 1.6 }}>
            Access is granted only after evaluation.
          </p>
          <div className="w-12 h-px mx-auto" style={{ background: C.accent, opacity: 0.4 }} />
        </motion.div>
      </section>
    );
  }

  const labelStyle = { color: C.muted, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400 };
  const inputStyle = {
    background: 'rgba(255,255,255,0.015)',
    borderColor: C.border,
    color: C.text,
    fontSize: '15px',
    height: '48px',
    borderRadius: 0,
  };
  const textareaBase = 'w-full font-[\'Outfit\'] p-3 focus:outline-none resize-none rounded-none transition-colors';

  return (
    <section
      ref={ref}
      id="access-gate"
      className="px-6 py-20 sm:py-24 flex items-center justify-center"
      data-testid="access-gate-section"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9 }}
        className="w-full max-w-xl space-y-8"
      >
        <div className="text-center space-y-3">
          <p className="font-['Outfit']" style={{ color: C.faint, fontSize: '12px', letterSpacing: '6px', textTransform: 'uppercase' }}>
            Request Access
          </p>
          <p
            className="font-['Outfit'] font-light"
            style={{ color: C.text, fontSize: '16px', lineHeight: 1.65, letterSpacing: '0.005em' }}
          >
            This system is not open access.<br />
            Each case is evaluated before entry.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="access-gate-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-['Outfit'] block mb-2" style={labelStyle}>Full Name *</label>
              <Input
                data-testid="gate-name"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                required
                placeholder="Your name"
                className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D]"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="font-['Outfit'] block mb-2" style={labelStyle}>Email *</label>
              <Input
                data-testid="gate-email"
                type="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                required
                placeholder="your@email.com"
                className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D]"
                style={inputStyle}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-['Outfit'] block mb-2" style={labelStyle}>Age</label>
              <Input
                data-testid="gate-age"
                type="number"
                value={form.age}
                onChange={e => update('age', e.target.value)}
                placeholder="Age"
                min={1}
                max={120}
                className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D]"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="font-['Outfit'] block mb-2" style={labelStyle}>City</label>
              <Input
                data-testid="gate-city"
                value={form.city}
                onChange={e => update('city', e.target.value)}
                placeholder="Your city"
                className="font-['Outfit'] focus:border-[#C89B7B] placeholder:text-[#6B737D]"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label className="font-['Outfit'] block mb-2" style={labelStyle}>Primary Concern</label>
            <textarea
              data-testid="gate-concern"
              value={form.condition}
              onChange={e => update('condition', e.target.value)}
              rows={2}
              placeholder="What brings you here"
              className={`${textareaBase} placeholder:text-[#6B737D]`}
              style={{ ...inputStyle, height: 'auto', border: `1px solid ${C.border}` }}
            />
          </div>

          <div>
            <label className="font-['Outfit'] block mb-2" style={labelStyle}>What have you already tried? *</label>
            <textarea
              data-testid="gate-tried"
              value={form.what_tried}
              onChange={e => update('what_tried', e.target.value)}
              rows={3}
              required
              placeholder="Treatments, approaches, specialists, duration..."
              className={`${textareaBase} placeholder:text-[#6B737D]`}
              style={{ ...inputStyle, height: 'auto', border: `1px solid ${C.border}` }}
            />
          </div>

          <Button
            data-testid="gate-submit"
            type="submit"
            disabled={submitting}
            className="w-full font-['Outfit'] font-medium rounded-none"
            style={{
              backgroundColor: C.accent,
              color: '#080C14',
              fontSize: '15px',
              height: '52px',
              letterSpacing: '2px',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit for Evaluation'}
          </Button>
        </form>

        <div className="flex items-center justify-center gap-3 pt-2">
          <img
            src="/brand/regenflo-wordmark.png"
            alt="ReGenflō"
            className="h-4 w-auto opacity-35"
            draggable={false}
          />
          <span className="font-['Outfit']" style={{ color: C.faint, fontSize: '11px', letterSpacing: '3px' }}>
            Lifely &middot; CARE OS
          </span>
        </div>
      </motion.div>
    </section>
  );
}

/* ─── MAIN PAGE ─── */
export default function LandingPage() {
  const scrollToGate = () => {
    document.getElementById('access-gate')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToReality = () => {
    document.getElementById('reality-break')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ background: C.bg, color: C.text, fontFamily: "'Outfit', sans-serif" }}
      data-testid="landing-page"
    >
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet" />
      <Header />
      <Hero onEnter={scrollToGate} onUnderstand={scrollToReality} />
      <RealityBreak />
      <SystemAxes />
      <SystemFlow />
      <SelfRecognition />
      <Philosophy />
      <AccessGate />
    </div>
  );
}
